import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import Keycloak from 'keycloak-js';
import { resetRedirectFlag, setKeycloakGetter } from '../lib/api';

const AuthContext = createContext(null);

// Create Keycloak instance outside component
let keycloakInstance = null;

const getKeycloakInstance = () => {
  if (!keycloakInstance) {
    keycloakInstance = new Keycloak({
      url: process.env.REACT_APP_KEYCLOAK_URL,
      realm: process.env.REACT_APP_KEYCLOAK_REALM,
      clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID,
    });
  }
  return keycloakInstance;
};

export const AuthProvider = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const keycloak = useRef(getKeycloakInstance()).current;
  const initialized = useRef(false);
  const refreshIntervalRef = useRef(null);

  // Provide keycloak getter to API module without global exposure
  useEffect(() => {
    // Use a getter function instead of exposing the instance globally
    const keycloakGetter = () => keycloak;
    setKeycloakGetter(keycloakGetter);

    return () => {
      setKeycloakGetter(null);
    };
  }, [keycloak]);

  useEffect(() => {
    // Prevent double initialization in React StrictMode
    if (initialized.current) return;
    initialized.current = true;

    const init = async () => {
      try {
        // Add timeout to prevent infinite loading
        const initPromise = keycloak.init({
          onLoad: 'check-sso',
          checkLoginIframe: false, // Disabled: iframe check causes redirect loops in modern browsers
          // (blocked by SameSite/third-party cookie policies)
        });

        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Keycloak initialization timeout')), 30000)
        );

        const isAuthenticated = await Promise.race([initPromise, timeoutPromise]);

        // Handle logout from other windows/tabs (Single Sign-Out)
        keycloak.onAuthLogout = () => {
          console.log('Portal: Logout detected from Keycloak');
          // Clear local state
          setAuthenticated(false);
          setUser(null);
          // Clear localStorage
          localStorage.clear();
          sessionStorage.clear();
          // Force reload to reset app state
          window.location.href = window.location.origin;
        };

        // Handle token refresh errors
        keycloak.onAuthRefreshError = () => {
          console.error('Portal: Token refresh failed, redirecting to login');
          setAuthenticated(false);
          setUser(null);
          keycloak.login();
        };

        if (isAuthenticated) {
          setAuthenticated(true);

          // Get user info from token (no need for additional API call)
          const tokenParsed = keycloak.tokenParsed;

          // Collect realm roles + all client roles
          const realmRoles = tokenParsed?.realm_access?.roles || [];
          const resourceAccess = tokenParsed?.resource_access || {};
          const clientRoles = Object.values(resourceAccess).flatMap(
            (resource) => resource.roles || []
          );
          const allRoles = [...new Set([...realmRoles, ...clientRoles])];

          setUser({
            id: keycloak.subject,
            username: tokenParsed?.preferred_username,
            email: tokenParsed?.email,
            firstName: tokenParsed?.given_name,
            lastName: tokenParsed?.family_name,
            roles: allRoles,
          });
        } else {
          // Not authenticated, redirect to login
          keycloak.login();
        }
      } catch (error) {
        console.error('[Auth] Keycloak initialization error:', error);

        // Let we forcefully redirect to login if initializing auth crashes (e.g timeout/cors issue)
        setAuthenticated(false);
        setUser(null);

        if (process.env.NODE_ENV !== 'production') {
          console.error('[Auth] Error details:', {
            message: error.message,
            url: process.env.REACT_APP_KEYCLOAK_URL,
            realm: process.env.REACT_APP_KEYCLOAK_REALM,
            clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID,
          });
        }

        setTimeout(() => {
          keycloak.login();
        }, 3000);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [keycloak]);

  // Reset redirect flag when authenticated (to allow future login redirects)
  useEffect(() => {
    if (authenticated) {
      resetRedirectFlag();
    }
  }, [authenticated]);

  // Auto-refresh token when authenticated (separate useEffect for proper cleanup)
  useEffect(() => {
    if (!authenticated) return;

    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    // Start new refresh interval
    refreshIntervalRef.current = setInterval(() => {
      keycloak.updateToken(70).catch((error) => {
        console.error('Failed to refresh token:', error);
        // Clear interval on failure (onAuthRefreshError will handle redirect)
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
      });
    }, 60000); // Check every 60 seconds

    // Cleanup function
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [authenticated, keycloak]);

  const login = () => {
    keycloak.login();
  };

  const logout = () => {
    setAuthenticated(false);
    setUser(null);
    keycloak.logout({
      redirectUri: window.location.origin,
    });
  };

  const getToken = () => {
    return keycloak.token;
  };

  const hasRole = (role) => {
    return user?.roles?.includes(role) || false;
  };

  const hasAnyRole = (roles) => {
    return roles.some((role) => hasRole(role));
  };

  const value = {
    authenticated,
    user,
    loading,
    keycloak,
    login,
    logout,
    getToken,
    hasRole,
    hasAnyRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};